import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FORMS } from "./formSchemas";
import "./forms.css";

const API = "/api";
const OTHER = "__other";

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// localStorage can throw (Safari private mode, blocked storage) — never let it break the form.
const draftKey = (slug) => `iyf-draft-${slug}`;
const readDraft = (slug) => { try { return JSON.parse(localStorage.getItem(draftKey(slug)) || "null"); } catch { return null; } };
const writeDraft = (slug, data) => { try { localStorage.setItem(draftKey(slug), JSON.stringify(data)); } catch { /* ignore */ } };
const clearDraft = (slug) => { try { localStorage.removeItem(draftKey(slug)); } catch { /* ignore */ } };

function initialValues(form) {
  const v = { name: "", email: "" };
  form.sections.forEach((s) => s.items.forEach((it) => {
    if (it.type === "checkbox") v[it.id] = [];
    else if (it.type === "grid") v[it.id] = {};
    else v[it.id] = it.defaultToday ? todayISO() : "";
    if (it.other) v[`${it.id}${OTHER}`] = "";
  }));
  return v;
}

function isEmpty(val) {
  if (Array.isArray(val)) return val.length === 0;
  if (val && typeof val === "object") return Object.keys(val).length === 0;
  return !String(val ?? "").trim();
}

// Flatten to the ordered [{id, q, a}] the server stores. "Other" text replaces the sentinel.
function buildAnswers(form, values) {
  const out = [];
  form.sections.forEach((s) => s.items.forEach((it) => {
    let a = values[it.id];
    const otherText = (values[`${it.id}${OTHER}`] || "").trim();
    if (it.type === "radio" && a === OTHER) a = otherText ? `Other: ${otherText}` : "Other";
    if (it.type === "checkbox") a = a.map((x) => (x === OTHER ? (otherText ? `Other: ${otherText}` : "Other") : x));
    out.push({ id: it.id, q: it.q, a: typeof a === "string" ? a.trim() : a });
  }));
  return out;
}

/* ─── Field renderers ─────────────────────────────────────── */

function Choice({ type, name, value, checked, onChange, label }) {
  return (
    <label className={`iyf-choice${checked ? " is-on" : ""}`}>
      <input type={type} name={name} value={value} checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function Field({ it, values, set, error }) {
  const val = values[it.id];
  const otherKey = `${it.id}${OTHER}`;
  const label = (
    <>
      {it.q}
      {it.required && <span className="iyf-req" aria-hidden="true"> *</span>}
    </>
  );

  let control;
  switch (it.type) {
    case "short":
      control = <input className="iyf-inp" id={it.id} value={val} onChange={(e) => set(it.id, e.target.value)} />;
      break;
    case "paragraph":
      control = <textarea className="iyf-textarea" id={it.id} rows={3} value={val} onChange={(e) => set(it.id, e.target.value)} />;
      break;
    case "date":
      control = <input className="iyf-inp iyf-inp--date" type="date" id={it.id} value={val} onChange={(e) => set(it.id, e.target.value)} />;
      break;
    case "radio":
      control = (
        <div className="iyf-choices">
          {it.options.map((o) => (
            <Choice key={o} type="radio" name={it.id} value={o} label={o} checked={val === o} onChange={() => set(it.id, o)} />
          ))}
          {it.other && <Choice type="radio" name={it.id} value={OTHER} label="Other" checked={val === OTHER} onChange={() => set(it.id, OTHER)} />}
        </div>
      );
      break;
    case "checkbox": {
      const toggle = (o) => set(it.id, val.includes(o) ? val.filter((x) => x !== o) : [...val, o]);
      control = (
        <div className={`iyf-choices${it.columns ? " iyf-choices--cols" : ""}`}>
          {it.options.map((o) => (
            <Choice key={o} type="checkbox" name={it.id} value={o} label={o} checked={val.includes(o)} onChange={() => toggle(o)} />
          ))}
          {it.other && <Choice type="checkbox" name={it.id} value={OTHER} label="Other" checked={val.includes(OTHER)} onChange={() => toggle(OTHER)} />}
        </div>
      );
      break;
    }
    case "scale":
      control = (
        <div className="iyf-scale">
          {it.low && <span className="iyf-scale__end">{it.low}</span>}
          <div className="iyf-scale__btns" role="radiogroup" aria-label={it.q}>
            {it.options.map((o) => (
              <button type="button" key={o} role="radio" aria-checked={val === o}
                className={`iyf-scale__btn${val === o ? " is-on" : ""}`}
                onClick={() => set(it.id, val === o ? "" : o)}>{o}</button>
            ))}
          </div>
          {it.high && <span className="iyf-scale__end">{it.high}</span>}
        </div>
      );
      break;
    case "grid": {
      const pick = (row, col) => {
        const next = { ...val };
        if (next[row] === col) delete next[row]; else next[row] = col;
        set(it.id, next);
      };
      control = (
        <div className="iyf-grid">
          <div className="iyf-grid__row iyf-grid__head" aria-hidden="true" style={{ "--cols": it.cols.length }}>
            <span />
            {it.cols.map((c) => <span key={c}>{c}</span>)}
          </div>
          {it.rows.map((r) => (
            <div key={r} className="iyf-grid__row" role="radiogroup" aria-label={r} style={{ "--cols": it.cols.length }}>
              <span className="iyf-grid__label">{r}</span>
              {it.cols.map((c) => (
                <button type="button" key={c} role="radio" aria-checked={val[r] === c} aria-label={`${r}: ${c}`}
                  className={`iyf-grid__cell${val[r] === c ? " is-on" : ""}`} onClick={() => pick(r, c)}>
                  <span className="iyf-grid__dot" />
                  <span className="iyf-grid__mobile">{c}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      );
      break;
    }
    default:
      control = null;
  }

  const wantsOther = it.other && (it.type === "radio" ? val === OTHER : Array.isArray(val) && val.includes(OTHER));
  const Tag = ["radio", "checkbox", "scale", "grid"].includes(it.type) ? "fieldset" : "div";
  const LabelTag = Tag === "fieldset" ? "legend" : "label";

  return (
    <Tag className={`iyf-q${error ? " has-error" : ""}`} data-field={it.id}>
      <LabelTag className="iyf-q__label" {...(LabelTag === "label" ? { htmlFor: it.id } : {})}>{label}</LabelTag>
      {it.help && <p className="iyf-q__help">{it.help}</p>}
      {control}
      {wantsOther && (
        <input className="iyf-inp iyf-inp--other" placeholder="Please specify" value={values[otherKey]}
          onChange={(e) => set(otherKey, e.target.value)} />
      )}
      {error && <p className="iyf-q__error">{error}</p>}
    </Tag>
  );
}

/* ─── Page ────────────────────────────────────────────────── */

// Keyed by slug so moving between forms (e.g. via footer links) starts fresh state.
export default function FormPage() {
  const { slug } = useParams();
  return <FormBody key={slug} slug={slug} />;
}

function hasRealAnswers(form, draft) {
  if (!draft) return false;
  const autoFilled = new Set(form.sections.flatMap((s) => s.items).filter((it) => it.defaultToday).map((it) => it.id));
  return Object.entries(draft).some(([k, v]) => !autoFilled.has(k) && !isEmpty(v));
}

function restoreValues(form, slug) {
  const v = { ...initialValues(form), ...(readDraft(slug) || {}) };
  // A restored draft must not carry an old session date.
  form.sections.forEach((s) => s.items.forEach((it) => { if (it.defaultToday) v[it.id] = todayISO(); }));
  return v;
}

function FormBody({ slug }) {
  const form = FORMS[slug];
  const [values, setValues] = useState(() => (form ? restoreValues(form, slug) : {}));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [serverError, setServerError] = useState("");
  const restored = useRef(!!form && hasRealAnswers(form, readDraft(slug)));
  const dirty = useRef(false); // only autosave once the person has entered something

  useEffect(() => {
    if (form) document.title = `${form.title} · Iyengar Yoga St. Petersburg`;
  }, [form]);

  // Autosave draft (debounced).
  useEffect(() => {
    if (!form || status === "done" || !dirty.current) return;
    const t = setTimeout(() => writeDraft(slug, values), 400);
    return () => clearTimeout(t);
  }, [values, slug, form, status]);

  const requiredItems = useMemo(
    () => (form ? form.sections.flatMap((s) => s.items).filter((it) => it.required) : []),
    [form]
  );

  if (!form) {
    return (
      <main className="iyf">
        <div className="iyf__inner iyf__done">
          <h1 className="iyf__title">Form not found</h1>
          <p className="iyf__sub">That link doesn't match one of our forms.</p>
          <Link className="iyf-btn" to="/">Back to home</Link>
        </div>
      </main>
    );
  }

  const set = (key, v) => {
    dirty.current = true;
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!values.name.trim()) e.name = "Please enter your name.";
    const em = values.email.trim();
    if (form.email === "required" && !em) e.email = "Please enter your email.";
    else if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = "Please enter a valid email.";
    requiredItems.forEach((it) => { if (isEmpty(values[it.id])) e[it.id] = "This question is required."; });
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      const first = document.querySelector(`[data-field="${Object.keys(e)[0]}"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(`${API}/forms/${slug}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          answers: buildAnswers(form, values),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong. Please try again.");
      clearDraft(slug);
      setStatus("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setServerError(err.message);
      setStatus("error");
    }
  };

  const resetDraft = () => {
    clearDraft(slug);
    restored.current = false;
    dirty.current = false;
    setValues(initialValues(form));
    setErrors({});
  };

  if (status === "done") {
    return (
      <main className="iyf">
        <div className="iyf__inner iyf__done">
          <p className="iyf__eyebrow">{form.eyebrow}</p>
          <h1 className="iyf__title">Thank you, {values.name.split(" ")[0]}</h1>
          <hr className="iyf__rule" />
          <p className="iyf__sub">
            Your {form.title.toLowerCase()} has been received.
            {values.email.trim() && " A confirmation is on its way to your inbox."}
          </p>
          <Link className="iyf-btn" to="/">Back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="iyf">
      <form className="iyf__inner" onSubmit={onSubmit} noValidate>
        <header className="iyf__header">
          <p className="iyf__eyebrow">{form.eyebrow}</p>
          <h1 className="iyf__title">{form.title}</h1>
          <hr className="iyf__rule" />
          <p className="iyf__sub">{form.description}</p>
        </header>

        {restored.current && (
          <div className="iyf-note">
            <span>We restored your saved answers from last time.</span>
            <button type="button" className="iyf-link" onClick={resetDraft}>Start over</button>
          </div>
        )}

        <section className="iyf-card iyf-card--fields">
          <div className={`iyf-field${errors.name ? " has-error" : ""}`} data-field="name">
            <label className="iyf-lbl" htmlFor="iyf-name">Name<span className="iyf-req"> *</span></label>
            <input className="iyf-inp" id="iyf-name" autoComplete="name" value={values.name} onChange={(e) => set("name", e.target.value)} />
            {errors.name && <p className="iyf-q__error">{errors.name}</p>}
          </div>
          <div className={`iyf-field${errors.email ? " has-error" : ""}`} data-field="email">
            <label className="iyf-lbl" htmlFor="iyf-email">
              Email{form.email === "required" ? <span className="iyf-req"> *</span> : <span className="iyf-opt"> (optional)</span>}
            </label>
            <input className="iyf-inp" id="iyf-email" type="email" autoComplete="email" value={values.email} onChange={(e) => set("email", e.target.value)} />
            {errors.email && <p className="iyf-q__error">{errors.email}</p>}
          </div>
        </section>

        {form.sections.map((s) => (
          <section className="iyf-card" key={s.title}>
            <h2 className="iyf-card__title">{s.title}</h2>
            {s.items.map((it) => (
              <Field key={it.id} it={it} values={values} set={set} error={errors[it.id]} />
            ))}
          </section>
        ))}

        {serverError && <p className="iyf-server-error" role="alert">{serverError}</p>}

        <div className="iyf__actions">
          <button type="submit" className="iyf-btn" disabled={status === "sending"}>
            {status === "sending" ? "Sending…" : "Submit"}
          </button>
        </div>
      </form>
    </main>
  );
}
